import React, { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";

/**
 * UEX-suggested pricing tiers for a listing form.
 *
 * Calls the uex-currency edge function (op=suggest_price) which:
 *   - Looks up the SC item name in UEX's items catalog (cached at the function)
 *   - Aggregates min / avg / max prices across all terminals
 *     • For items: uses BUY prices (terminal sale price = market rate to acquire)
 *     • For commodities: uses SELL prices (per-SCU resale price)
 *   - Falls back to commodities_prices_all when the item name doesn't match
 *
 * Renders three buttons (Low / Medium / High). Clicking a button hands the
 * suggested aUEC value to the parent via onApply, which decides whether to
 * fill the manual aUEC field or compute a Merit count from the org's peg.
 */
const EDGE_URL = `${process.env.REACT_APP_SUPABASE_URL || "https://negtawswkqbvvwobozdh.supabase.co"}/functions/v1/uex-currency`;

export default function SuggestedPricing({ itemName, meritPegAuec, pricingMode, onApply, accent = "#fcd657" }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!itemName || !itemName.trim()) { setData(null); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const url = `${EDGE_URL}?op=suggest_price&name=${encodeURIComponent(itemName)}`;
        const res = await fetch(url, {
          headers: {
            Accept: "application/json",
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
        });
        const env = await res.json().catch(() => null);
        if (cancelled) return;
        if (!res.ok || !env?.ok) throw new Error(env?.error || `HTTP ${res.status}`);
        setData(env);
      } catch (e) {
        if (!cancelled) setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [itemName]);

  if (!itemName) return null;

  if (loading) {
    return (
      <div style={{ padding: "8px 12px", border: `1px dashed ${accent}33`, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.2, color: "rgba(232,234,246,0.45)", textTransform: "uppercase" }}>
        Looking up UEX pricing for {itemName}…
      </div>
    );
  }

  if (error || !data?.found) {
    let msg = "No UEX pricing data for this item. Set price manually.";
    if (data?.matched_name && data?.reason === "no_price_data") {
      msg = `UEX has "${data.matched_name}" in catalog but no terminals currently stock it (${data.terminal_rows ?? 0} rows). Set price manually.`;
    } else if (error) {
      msg = `UEX lookup failed: ${error}`;
    }
    return (
      <div style={{ padding: "8px 12px", border: `1px dashed rgba(255,255,255,0.08)`, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.2, color: "rgba(232,234,246,0.35)" }}>
        {msg}
      </div>
    );
  }

  const tiers = [
    { key: "low", label: "Low", value: data.low, color: "#34d399", hint: "min terminal price" },
    { key: "medium", label: "Medium", value: data.medium, color: accent, hint: "market average" },
    { key: "high", label: "High", value: data.high, color: "#ef5350", hint: "max terminal price" },
  ];
  const unitLabel = data.unit === "per SCU" ? "aUEC/SCU" : "aUEC";
  const sideLabel = data.side === "buy" ? "terminal sale price" : data.side === "sell" ? "terminal buyback" : "";

  return (
    <div style={{ padding: "10px 12px", border: `1px solid ${accent}33`, background: `${accent}06` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 6 }}>
        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.8, color: accent, textTransform: "uppercase" }}>
          UEX Suggested Pricing
        </span>
        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, color: "rgba(232,234,246,0.45)", textTransform: "uppercase" }}>
          {data.matched_name && data.matched_name !== itemName ? `matched: ${data.matched_name} · ` : ""}
          {data.sample_size} terminal{data.sample_size === 1 ? "" : "s"}
          {sideLabel ? ` · ${sideLabel}` : ""}
          {data.unit ? ` · ${data.unit}` : ""}
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
        {tiers.map(t => {
          const meritEquiv = pricingMode === "merit_linked" && meritPegAuec ? Math.max(1, Math.round(t.value / meritPegAuec)) : null;
          return (
            <button key={t.key} type="button" onClick={() => onApply(t.value, t.key)}
              title={`${t.hint}: ${Number(t.value).toLocaleString()} ${unitLabel}`}
              style={{
                padding: "8px 10px", textAlign: "left",
                background: "transparent", border: `1px solid ${t.color}55`, color: t.color,
                cursor: "pointer", fontFamily: "'Orbitron', sans-serif",
              }}>
              <div style={{ fontSize: 9, letterSpacing: 1.8, textTransform: "uppercase", opacity: 0.8 }}>{t.label}</div>
              <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }}>{Number(t.value).toLocaleString()}</div>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, color: "rgba(232,234,246,0.45)", marginTop: 2, textTransform: "uppercase" }}>
                {pricingMode === "merit_linked" && meritEquiv != null ? `${meritEquiv} Merits` : unitLabel}
              </div>
            </button>
          );
        })}
      </div>
      <div style={{ marginTop: 6, fontFamily: "'Rajdhani', sans-serif", fontSize: 12, color: "rgba(232,234,246,0.5)" }}>
        Click any tier to fill the price below. You can still edit it afterward.
      </div>
    </div>
  );
}
